<template>
  <div class="flex flex-col items-end gap-5">
    <DatePicker
      :model-value="dateRange"
      default-value="180d"
      class="order-2 w-full desktop:order-1 desktop:w-[300px]"
      @update:model-value="changeDateRange"
    />
    <div class="order-1 size-full desktop:order-2">
      <highcharts
        ref="chartRef"
        :options="options"
      />
    </div>
  </div>
</template>
  
<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */

import type { TooltipFormatterContextObject } from 'highcharts'
import { ref } from 'vue'

import type { LivewatchCirculatingSupplyTracker } from '~/api/service/livewatch/circulating-supply-tracker.service'
import { SVG_BASE_URL } from '~/constants/image.constant'
import { setCommaWithDigits } from '~/utils/numberFormat'

import DatePicker from './date-picker/DatePicker.vue'

interface IProps {
  series: {
    circulatingSupplyList: LivewatchCirculatingSupplyTracker['circulatingSupplyList'];
    scheduleSupplyList: LivewatchCirculatingSupplyTracker['scheduleSupplyList'];
  }
  unit: string
}

const props = defineProps<IProps>()

const dateRange = ref<[Date | null, Date | null]>([ null, null ])

const chartRef = ref()
const { createCustomTooltip } = useChartTooltip()

const issuePoints = computed(() => {
  const circulating = props.series.circulatingSupplyList
  const schedule = props.series.scheduleSupplyList
  const scheduleMap = new Map(schedule.map(([ x, y ]) => [ x, y ]))
  const issuePeriods: { start: number, end?: number }[] = []
  let inIssue = false
  let issueStart: number | null = null

  for (const [ x, circulatingY ] of circulating) {
    const scheduleY = scheduleMap.get(x)
    if (scheduleY === undefined) continue

    // 감도 5% 적용
    const threshold = circulatingY * 0.95

    if (!inIssue && scheduleY < threshold) {
      // 문제 발생
      inIssue = true
      issueStart = x
    } else if (inIssue && scheduleY >= threshold) {
      // 문제 해결
      inIssue = false
      issuePeriods.push({ start: issueStart as number, end: x })
      issueStart = null
    }
  }
  if (inIssue && issueStart !== null) {
    issuePeriods.push({ start: issueStart })
  }

  // 발생/해결 포인트 추출
  const startPoints: [number, number][] = []
  const endPoints: [number, number][] = []
  for (const period of issuePeriods) {
    if (period.start !== undefined) {
      const y = scheduleMap.get(period.start) ?? null
      if (y !== null) startPoints.push([ period.start, y ])
    }
    if (period.end !== undefined) {
      const y = scheduleMap.get(period.end) ?? null
      if (y !== null) endPoints.push([ period.end, y ])
    }
  }
  return { startPoints, endPoints }
})

const changeDateRange = (dateRange: [Date | null, Date | null]) => {
  const chart = chartRef.value?.chart || chartRef.value
  if (!chart) return

  const series = chart.series[0]
  if (!series?.data?.length) return

  // 실제 데이터 범위
  const dataPoints = series.data
  const actualStartDate = dataPoints[0].x || dataPoints[0][0]
  const actualEndDate = dataPoints[dataPoints.length - 1].x || dataPoints[dataPoints.length - 1][0]

  // 선택된 날짜 범위
  let startDate = dateRange[0] ? new Date(dateRange[0]).setHours(0, 0, 0, 0) : actualStartDate
  let endDate = dateRange[1] ? new Date(dateRange[1]).setHours(23, 59, 59, 999) : actualEndDate

  // 데이터 범위를 벗어나지 않도록 제한
  startDate = Math.max(startDate, actualStartDate)
  endDate = Math.min(endDate, actualEndDate)

  // 선택된 기간이 데이터보다 길면 전체 데이터 범위 사용
  const selectedRange = endDate - startDate
  const dataRange = actualEndDate - actualStartDate
  
  if (selectedRange > dataRange) {
    startDate = actualStartDate
    endDate = actualEndDate
  }

  chart.xAxis[0].setExtremes(startDate, endDate)
}

const options = computed(() => {
  return {
    title: undefined,
    chart: {
      width: null,
      backgroundColor: 'transparent',
      zooming: {
        mouseWheel: {
          enabled: false
        }
      }
    },
    xAxis: {
      type: 'datetime',
      minTickInterval: 24 * 3600 * 1000,
      dateTimeLabelFormats: {
        millisecond: '%H:%M:%S.%L',
        second: '%H:%M:%S',
        minute: '%H:%M', 
        hour: '%H:%M',
        day: '%m/%d',
        week: '%m/%d',
        month: '%Y-%m',
        year: '%Y'
      },
      labels: {
        style: {
          color: '#FFFFFF'
        }
      },
      lineColor: '#2C3241',
      tickColor: '#2C3241',
      gridLineColor: '#2C3241',
      crosshair: {
        color: '#FFFFFF'
      }
    },
    yAxis: {
      visible: true,
      title: {
        text: null
      },
      opposite: true,
      labels: {
        style: {
          color: '#FFFFFF'
        },
        align: 'left',
      },
      gridLineColor: '#B0B8D033'
    },
    series: [
      {
        type: 'area',
        data: props.series.circulatingSupplyList,
        color: '#5C8FFA',
        // color: '#777C8B',
        name: 'Circulating Supply',
        opacity: 0.5,
        marker: {
          enabled: false
        },
        dataGrouping: {
          enabled: false,
        },
      },
      {
        type: 'line',
        data: props.series.scheduleSupplyList,
        name: 'Scheduled Supply',
        color: '#E6E6E6',
        // color: '#5C8FFA',
        opacity: 0.8,
        marker: {
          enabled: false
        },
        dataGrouping: {
          enabled: false,
        },
      },
      {
        type: 'scatter',
        data: issuePoints.value.startPoints,
        name: 'Warning',
        color: '#FF4B4B',
        marker: {
          symbol: `url(${SVG_BASE_URL}/lw-warning.svg)`,
          radius: 6,
          width: 16,
          height: 16,
        },
        showInLegend: false,
        enableMouseTracking: false,
        dataGrouping: { enabled: false }
      },
      {
        type: 'scatter',
        data: issuePoints.value.endPoints,
        name: 'Resolve',
        color: '#00C896',
        marker: {
          symbol: `url(${SVG_BASE_URL}/lw-verified.svg)`,
          radius: 6,
          width: 16,
          height: 16,
        },
        showInLegend: false,
        enableMouseTracking: false,
        dataGrouping: { enabled: false }
      }
    ],
    legend: {
      useHTML: true,
      labelFormatter: function (this: any) {
        return `<span style="color:#fff;">${this.name}</span>`
      }
    },
    scrollbar: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    navigator: {
      enabled: false
    },
    rangeSelector: {
      enabled: false,
    },
    tooltip: {
      split: false,
      shared: true,
      shadow: false,
      outside: true,
      useHTML: true,
      padding: 8,
      backgroundColor: 'none',
      borderWidth: 0,
      borderRadius: 0,
      style: {
        fontSize: '12px',
        fontFamily: 'Montserrat',
      },
      formatter: function (this: TooltipFormatterContextObject) {
        return createCustomTooltip(this, {
          containerClass: 'w-[450px] max-w-[90dvw]',
          date: (timestamp) => `<p class="font-bold">${new Date(timestamp).toISOString().slice(0, 10)}</p>`,
          content: (points: any | any[]) => {
            const chart = chartRef.value?.chart || chartRef.value
            if (!chart) return ''
            
            const firstPoint = Array.isArray(points) ? points[0] : points
            const xValue = firstPoint?.x ?? null
            if (xValue === null) return ''

            const aggregatedPoints = chart.series
              .filter((s: any) => s.visible && s.type !== 'scatter')
              .map((s: any) => {
                const matched = s.data.find((p: any) => p.x === xValue)
                return matched ? { series: s, y: matched.y, color: s.color || matched.color } : null
              })
              .filter(Boolean) as any[]

            return aggregatedPoints.map(point =>
              `<div class="flex w-full items-center justify-between">
                <p><span style="color:${point.color};">● </span>${point.series.name}</p>
                <div class="font-bold">${setCommaWithDigits(point.y, 2)} NXPC</div>
              </div>`
            ).join('')
          }
        })
      }
    },
  }
})
</script>

<style lang="scss" scoped>
</style>